import {
  businessNetworkEventDateKey,
  businessNetworkEventEndDate,
  formatBusinessNetworkEventAddress,
  formatBusinessNetworkEventDate,
  formatBusinessNetworkEventTime,
  londonDateKey,
} from "../businessNetworkEvents";
import { eventDetailPath } from "../eventRoutes";
import {
  HOUSING_DEVELOPMENT_EVENT_SERIES,
  HOUSING_DEVELOPMENT_PROGRAMME,
  HOUSING_DEVELOPMENT_SOURCE_VALUES,
  NOVEMBER_HOUSING_EVENT_ID,
} from "./config";

export const cleanHousingDevelopmentValue = (value) =>
  String(value ?? "").trim();

const normalisedKey = (value) =>
  cleanHousingDevelopmentValue(value).toLowerCase();

export function normaliseHousingDevelopmentSource(value) {
  const source = normalisedKey(value);
  return HOUSING_DEVELOPMENT_SOURCE_VALUES.includes(source)
    ? source
    : "unknown";
}

export function normaliseSurveyQuestionType(value) {
  const key = normalisedKey(value).replace(/[^a-z0-9]+/g, "");
  const types = {
    shorttext: "shortText",
    longtext: "longText",
    textarea: "longText",
    email: "email",
    phone: "phone",
    yesno: "yesNo",
    singlechoice: "singleChoice",
    multiplechoice: "multipleChoice",
    multiselect: "multipleChoice",
    dropdown: "dropdown",
    ratingscale: "ratingScale",
    rating: "ratingScale",
    rating15: "rating1To5",
    rating1to5: "rating1To5",
    scale15: "rating1To5",
    rating110: "rating1To10",
    rating1to10: "rating1To10",
    scale110: "rating1To10",
    date: "date",
    demographic: "demographic",
  };
  return types[key] || "shortText";
}

export function isHousingDevelopmentEvent(event = {}, eventId = "") {
  const record = event || {};
  const resolvedEventId = cleanHousingDevelopmentValue(
    record.id || record.eventId || eventId
  );
  return resolvedEventId === NOVEMBER_HOUSING_EVENT_ID || [
    record.programme,
    record.eventCategory,
    record.eventSeries,
    record.eventType,
    record.type,
  ].some((value) =>
    [HOUSING_DEVELOPMENT_PROGRAMME, HOUSING_DEVELOPMENT_EVENT_SERIES].includes(
      normalisedKey(value)
    )
  );
}

export function isHousingDevelopmentSurvey(survey = {}) {
  return normalisedKey(survey.programme) === HOUSING_DEVELOPMENT_PROGRAMME;
}

function surveyBoundary(value) {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return { dateOnly: value.trim(), instant: null, valid: true };
  }
  if (!value) return { dateOnly: "", instant: null, valid: true };
  const instant = typeof value?.toDate === "function"
    ? value.toDate()
    : value instanceof Date
    ? value
    : new Date(value);
  return {
    dateOnly: "",
    instant,
    valid: instant instanceof Date && !Number.isNaN(instant.getTime()),
  };
}

export function housingDevelopmentSurveyAvailability(survey = {}, now = new Date()) {
  const reference = now instanceof Date && !Number.isNaN(now.getTime())
    ? now
    : new Date();
  const today = londonDateKey(reference);
  const open = surveyBoundary(survey.openDate || survey.openAt);
  const close = surveyBoundary(survey.closeDate || survey.closeAt);
  if (!open.valid || !close.valid) return { available: false, reason: "invalid-window" };
  if (
    (open.dateOnly && today < open.dateOnly)
    || (open.instant && reference.getTime() < open.instant.getTime())
  ) {
    return { available: false, reason: "not-open" };
  }
  // Date-only closing dates are inclusive in Europe/London; timestamp closing
  // values remain exact instants.
  if (
    (close.dateOnly && today > close.dateOnly)
    || (close.instant && reference.getTime() >= close.instant.getTime())
  ) {
    return { available: false, reason: "closed" };
  }
  return { available: true, reason: "open" };
}

export function isPublicHousingDevelopmentSurvey(survey = {}) {
  const status = normalisedKey(survey.status);
  return (
    survey.active === true &&
    survey.published !== false &&
    !["closed", "draft", "unpublished", "archived"].includes(status) &&
    isHousingDevelopmentSurvey(survey) &&
    housingDevelopmentSurveyAvailability(survey).available
  );
}

function eventSortKey(event = {}) {
  const date = businessNetworkEventDateKey(event);
  const time = cleanHousingDevelopmentValue(
    event.startTime || event.time || "00:00"
  );
  return `${date}T${/^\d{2}:\d{2}$/.test(time) ? time : "00:00"}`;
}

export function partitionHousingDevelopmentEvents(
  events = [],
  now = new Date()
) {
  const today = londonDateKey(now);
  const records = events
    .filter(
      (event) =>
        event?.status === "approved" && isHousingDevelopmentEvent(event)
    )
    .filter(
      (event) =>
        !["cancelled"].includes(
          normalisedKey(event.lifecycleStatus || event.eventLifecycle)
        )
    );

  const upcomingEvents = records
    .filter((event) => {
      const dateKey = businessNetworkEventDateKey(event);
      const end = businessNetworkEventEndDate(event);
      const lifecycle = normalisedKey(
        event.lifecycleStatus || event.eventLifecycle
      );
      return (
        dateKey &&
        dateKey >= today &&
        (!end || end.getTime() > now.getTime()) &&
        !["completed", "past"].includes(lifecycle)
      );
    })
    .sort((left, right) =>
      eventSortKey(left).localeCompare(eventSortKey(right), "en-GB")
    );

  const pastEvents = records
    .filter((event) => {
      const dateKey = businessNetworkEventDateKey(event);
      const end = businessNetworkEventEndDate(event);
      const lifecycle = normalisedKey(
        event.lifecycleStatus || event.eventLifecycle
      );
      return (
        ["completed", "past"].includes(lifecycle) ||
        Boolean(dateKey && dateKey < today) ||
        Boolean(end && end.getTime() <= now.getTime())
      );
    })
    .sort((left, right) =>
      eventSortKey(right).localeCompare(eventSortKey(left), "en-GB")
    );

  return {
    nextEvent: upcomingEvents[0] || null,
    upcomingEvents,
    futureEvents: upcomingEvents.slice(1),
    pastEvents,
  };
}

export function housingDevelopmentEventPath(event = {}) {
  const preservedPath = cleanHousingDevelopmentValue(
    event.publicPath || event.legacyPath
  );
  if (preservedPath.startsWith("/") && !preservedPath.startsWith("//")) {
    return preservedPath;
  }
  return eventDetailPath(event) || "/housing-development";
}

export function housingDevelopmentSurveyPath(survey = {}) {
  const preservedPath = cleanHousingDevelopmentValue(survey.publicPath);
  if (preservedPath.startsWith("/") && !preservedPath.startsWith("//")) {
    return preservedPath;
  }
  const slug = cleanHousingDevelopmentValue(
    survey.slug || survey.surveySlug || survey.id || survey.surveyId
  )
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug
    ? `/housing-development/surveys/${slug}`
    : "/housing-development";
}

export function housingDevelopmentEventSurveyLinks(event = {}) {
  const records = [];
  if (Array.isArray(event.surveys)) records.push(...event.surveys);
  if (Array.isArray(event.surveySlugs)) {
    records.push(...event.surveySlugs.map((slug) => ({ slug })));
  }
  if (Array.isArray(event.surveyIds)) {
    records.push(...event.surveyIds.map((surveyId) => ({ surveyId })));
  }
  if (event.surveySlug || event.surveyId || event.surveyPath) {
    records.unshift({
      slug: event.surveySlug,
      surveyId: event.surveyId,
      publicPath: event.surveyPath,
      title: event.surveyTitle,
    });
  }

  const seen = new Set();
  return records.reduce((links, value) => {
    const record = typeof value === "string" ? { slug: value } : value || {};
    const path = housingDevelopmentSurveyPath(record);
    if (!path.startsWith("/housing-development/surveys/") || seen.has(path)) {
      return links;
    }
    seen.add(path);
    const slug = surveySlugFromRecord(record) || path.split("/").filter(Boolean).pop();
    links.push({
      id: cleanHousingDevelopmentValue(record.id || record.surveyId || slug),
      slug,
      path,
      title: cleanHousingDevelopmentValue(
        record.title || record.surveyName || record.label
      ),
    });
    return links;
  }, []);
}

export function publicHousingDevelopmentEventSurveyLinks(event = {}, surveys = []) {
  const configuredLinks = housingDevelopmentEventSurveyLinks(event);
  const configuredKeys = new Set(configuredLinks.flatMap((link) => [
    cleanHousingDevelopmentValue(link.id).toLowerCase(),
    cleanHousingDevelopmentValue(link.slug).toLowerCase(),
  ]).filter(Boolean));
  const eventId = cleanHousingDevelopmentValue(event.id || event.eventId);
  const seen = new Set();
  return (Array.isArray(surveys) ? surveys : [])
    .filter((survey) => isPublicHousingDevelopmentSurvey(survey))
    .filter((survey) => {
      const id = cleanHousingDevelopmentValue(survey.id || survey.surveyId).toLowerCase();
      const slug = surveySlugFromRecord(survey).toLowerCase();
      const eventIds = Array.isArray(survey.eventIds)
        ? survey.eventIds.map(cleanHousingDevelopmentValue)
        : [cleanHousingDevelopmentValue(survey.eventId)];
      return configuredKeys.has(id)
        || configuredKeys.has(slug)
        || (eventId && eventIds.includes(eventId));
    })
    .reduce((links, survey) => {
      const path = housingDevelopmentSurveyPath(survey);
      if (seen.has(path)) return links;
      seen.add(path);
      links.push({
        id: cleanHousingDevelopmentValue(survey.id || survey.surveyId),
        slug: surveySlugFromRecord(survey),
        path,
        title: cleanHousingDevelopmentValue(
          survey.title || survey.surveyName || survey.name
        ),
      });
      return links;
    }, []);
}

export function surveySlugFromRecord(survey = {}) {
  return cleanHousingDevelopmentValue(
    survey.slug || survey.surveySlug || survey.id || survey.surveyId
  );
}

export function mergeMissingSeedRecords(records = [], seeds = [], enabled = false) {
  const publicRecords = Array.isArray(records) ? records.filter(Boolean) : [];
  if (!enabled) return publicRecords;

  const knownIds = new Set(
    publicRecords.map((record) =>
      cleanHousingDevelopmentValue(record.id || record.surveyId)
    )
  );
  return [
    ...publicRecords,
    ...seeds.filter(
      (seed) =>
        seed &&
        !knownIds.has(cleanHousingDevelopmentValue(seed.id || seed.surveyId))
    ),
  ];
}

export function createInitialSurveyAnswers(survey = {}) {
  return Object.fromEntries(
    (Array.isArray(survey.questions) ? survey.questions : []).map((question) => [
      question.id,
      normaliseSurveyQuestionType(question.type) === "multipleChoice" ? [] : "",
    ])
  );
}

function answerIsEmpty(value) {
  return Array.isArray(value)
    ? value.length === 0
    : cleanHousingDevelopmentValue(value) === "";
}

function allowedOptions(question = {}) {
  return new Set(
    (Array.isArray(question.options) ? question.options : []).map((option) =>
      cleanHousingDevelopmentValue(
        typeof option === "string" ? option : option?.value || option?.label
      )
    )
  );
}

export function normaliseHousingSurveyAnswers(survey = {}, answers = {}) {
  const questions = Array.isArray(survey.questions) ? survey.questions : [];
  return Object.fromEntries(
    questions.map((question) => {
      const answer = answers?.[question.id];
      const type = normaliseSurveyQuestionType(question.type);
      if (type === "multipleChoice") {
        return [
          question.id,
          Array.isArray(answer)
            ? answer.map(cleanHousingDevelopmentValue).filter(Boolean)
            : [],
        ];
      }
      if (["ratingScale", "rating1To5", "rating1To10"].includes(type)) {
        const value = Number(answer);
        return [question.id, Number.isFinite(value) ? value : ""];
      }
      return [question.id, cleanHousingDevelopmentValue(answer)];
    })
  );
}

export function validateHousingSurveyAnswers(survey = {}, answers = {}) {
  const errors = {};
  const questions = Array.isArray(survey.questions) ? survey.questions : [];

  questions.forEach((question) => {
    const id = cleanHousingDevelopmentValue(question.id);
    if (!id) return;
    const value = answers?.[id];
    const label = cleanHousingDevelopmentValue(question.label) || "This question";
    const type = normaliseSurveyQuestionType(question.type);

    if (question.required && answerIsEmpty(value)) {
      errors[id] = "Answer this required question.";
      return;
    }
    if (answerIsEmpty(value)) return;

    if (type === "email") {
      const email = cleanHousingDevelopmentValue(value);
      if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors[id] = "Enter a valid email address.";
      }
      return;
    }

    if (type === "phone") {
      const phone = cleanHousingDevelopmentValue(value);
      if (phone.length < 7 || phone.length > 40) {
        errors[id] = "Enter a valid phone number, or leave this field blank.";
      }
      return;
    }

    if (["singleChoice", "yesNo", "dropdown", "demographic"].includes(type)) {
      const options = allowedOptions(question);
      if (options.size > 0 && !options.has(cleanHousingDevelopmentValue(value))) {
        errors[id] = `Choose an available answer for ${label}.`;
      }
      return;
    }

    if (type === "multipleChoice") {
      const options = allowedOptions(question);
      const selections = Array.isArray(value)
        ? value.map(cleanHousingDevelopmentValue).filter(Boolean)
        : [];
      if (selections.some((selection) => !options.has(selection))) {
        errors[id] = `Choose only available answers for ${label}.`;
      } else if (
        Number.isFinite(Number(question.maxSelections)) &&
        selections.length > Number(question.maxSelections)
      ) {
        errors[id] = `Choose no more than ${question.maxSelections} answers.`;
      }
      return;
    }

    if (["ratingScale", "rating1To5", "rating1To10"].includes(type)) {
      const rating = Number(value);
      const minimum = Number(question.min || 1);
      const maximum = Number(
        question.max || (type === "rating1To10" ? 10 : 5)
      );
      if (!Number.isInteger(rating) || rating < minimum || rating > maximum) {
        errors[id] = `Choose a rating from ${minimum} to ${maximum}.`;
      }
      return;
    }

    if (type === "date") {
      const date = cleanHousingDevelopmentValue(value);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        errors[id] = "Enter a valid date.";
      }
      return;
    }

    const answer = cleanHousingDevelopmentValue(value);
    const maxLength = Number(question.maxLength || 2000);
    if (answer.length > maxLength) {
      errors[id] = `Keep this answer to ${maxLength} characters or fewer.`;
    }
  });

  return errors;
}

export function housingDevelopmentPartners(...collections) {
  const names = collections.flatMap((records) =>
    (Array.isArray(records) ? records : []).flatMap((record) =>
      Array.isArray(record?.partnerOrganisations)
        ? record.partnerOrganisations
        : Array.isArray(record?.organisations)
        ? record.organisations
        : []
    )
  );
  return [...new Set(names.map(cleanHousingDevelopmentValue).filter(Boolean))].sort(
    (left, right) => left.localeCompare(right, "en-GB")
  );
}

export {
  businessNetworkEventDateKey as housingDevelopmentEventDateKey,
  businessNetworkEventEndDate as housingDevelopmentEventEndDate,
  formatBusinessNetworkEventAddress as formatHousingDevelopmentEventAddress,
  formatBusinessNetworkEventDate as formatHousingDevelopmentEventDate,
  formatBusinessNetworkEventTime as formatHousingDevelopmentEventTime,
};

